import {
  isWebserverTelemetryEnabled,
  seeqFullReleaseIdentifier,
  seeqVersion,
} from '@/services/systemConfiguration.utilities';
import { DevConsoleExporter, PeriodicExportingMetricReader } from '@seeqdev/telemetry';
import { OTLPMetricExporter } from '@seeqdev/telemetry/otlp';
import type { TelemetryConfig } from '@seeqdev/telemetry';

const isDev = process.env.NODE_ENV !== 'production';

// Same-origin endpoint nginx reverse-proxies to the OTel Collector.
const OTLP_METRICS_ENDPOINT = '/otel/v1/metrics';

/**
 * Builds the metric readers for this session.
 *
 * Both dev and production export to the OTel Collector at `/otel/v1/metrics` so
 * RUM metrics can be validated locally against the Grafana stack. Only `url` is
 * configured; the export interval is left at the SDK default (60 s) and the
 * browser OTLP transport sends via keepalive fetch, which — together with the
 * `forceFlush()` on pagehide wired in `TelemetryProvider` — delivers the
 * end-of-session Core Web Vitals (INP/CLS finalize on hide).
 *
 * Dev additionally logs metrics to the browser console (`DevConsoleExporter`)
 * on a short interval for quick feedback while developing; production exports
 * only over OTLP.
 */
function createMetricReaders(): TelemetryConfig['readers'] {
  const readers: TelemetryConfig['readers'] = [
    new PeriodicExportingMetricReader({ exporter: new OTLPMetricExporter({ url: OTLP_METRICS_ENDPOINT }) }),
  ];

  if (isDev) {
    readers.push(
      new PeriodicExportingMetricReader({ exporter: new DevConsoleExporter(), exportIntervalMillis: 10_000 }),
    );
  }

  return readers;
}

/**
 * Resolves the telemetry config for this session from server-provided values,
 * or `null` when webserver telemetry is disabled. Must be called only after
 * configuration has loaded (the getters throw otherwise).
 *
 * Webserver (RUM) telemetry is gated on its own server flag
 * (Features/Telemetry/Webserver/Enabled), independent of the Features/Telemetry/Enabled
 * flag that gates product-usage telemetry, so the two can be opted into separately.
 */
export function resolveTelemetryConfig(): TelemetryConfig | null {
  if (!isWebserverTelemetryEnabled()) {
    return null;
  }

  return {
    serviceName: 'webserver',
    serviceVersion: seeqFullReleaseIdentifier() ?? seeqVersion() ?? 'unknown',
    // deployment.environment is intentionally omitted: the OTel Collector stamps
    // it from the k8s cluster on ingest. Local runs simply have no environment.
    readers: createMetricReaders(),
  };
}
